import { forwardRef, type ButtonHTMLAttributes, type CSSProperties } from 'react';

const variants = {
  primary: 'font-medium text-white',
  secondary: 'font-medium',
  ghost: 'font-normal',
  danger: 'font-medium',
} as const;

const sizes = {
  sm: 'h-7 px-2.5 text-xs rounded gap-1.5',
  md: 'h-8 px-3 text-sm rounded-md gap-2',
  lg: 'h-10 px-4 text-sm rounded-md gap-2',
} as const;

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: keyof typeof variants;
  size?: keyof typeof sizes;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = 'primary', size = 'md', className = '', children, style, ...props }, ref) => {
    const variantStyles: Record<keyof typeof variants, CSSProperties> = {
      primary: {
        background: 'var(--color-accent)',
        color: '#ffffff',
      },
      secondary: {
        background: 'var(--color-surface)',
        color: 'var(--color-ink)',
        border: '1px solid var(--color-line-heavy)',
      },
      ghost: {
        background: 'transparent',
        color: 'var(--color-ink-muted)',
      },
      danger: {
        background: 'transparent',
        color: 'var(--color-danger)',
      },
    };

    return (
      <button
        ref={ref}
        className={`inline-flex items-center justify-center transition-all duration-150 active:scale-[0.97] disabled:pointer-events-none disabled:opacity-50 ${variants[variant]} ${sizes[size]} ${variant === 'primary' ? 'hover:brightness-[0.97]' : ''} ${variant === 'ghost' || variant === 'danger' ? 'hover:bg-[var(--color-surface-3)]' : ''} ${className}`}
        style={{ ...variantStyles[variant], ...style }}
        {...props}
      >
        {children}
      </button>
    );
  },
);
Button.displayName = 'Button';
